import { Link } from '@tanstack/react-router';
import { RefreshCw, ArrowRight, Clock3 } from 'lucide-react';
import { useEffect } from 'react';
import { AppShell } from '@/components/AppShell';
import { Button } from '@/components/ui/button';
import { useStopSelection, useStopSelectionActions } from './state';

const seattleDate = (value: string | null | undefined, options: Intl.DateTimeFormatOptions) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Time unavailable';
  return new Intl.DateTimeFormat('en-US', { timeZone: 'America/Los_Angeles', ...options }).format(date);
};

export function BusTimesPage() {
  const snapshot = useStopSelection((current) => current);
  const actions = useStopSelectionActions();

  useEffect(() => {
    if (snapshot.matches('idle')) actions.loadView();
  }, [actions, snapshot]);

  const view = snapshot.context.view;
  const stop = view?.stops?.find((item) => item?.stopId === view.stopId);
  const route = view?.routes?.find((item) => item?.routeId === view.routeId);
  const direction = view?.directions?.find((item) => item?.directionId === view.directionId);
  const hasSelection = Boolean(view?.stopSelectionId && view.stopId);

  return (
    <AppShell title="Bus times" description="A clear look at the next scheduled departures from your stop.">
      {snapshot.matches('loading') ||
      snapshot.matches('choosingRoute') ||
      snapshot.matches('choosingDirection') ||
      snapshot.matches('choosingStop') ||
      snapshot.matches('refreshing') ? (
        <div className="rounded-[14px] border border-border bg-card p-8 text-muted-foreground" role="status">
          <div className="flex items-center gap-3">
            <Clock3 className="size-5 animate-pulse text-primary" /> Loading scheduled buses…
          </div>
        </div>
      ) : snapshot.matches('error') ? (
        <div className="rounded-[14px] border border-destructive/30 bg-card p-8">
          <h2 className="font-heading text-xl font-semibold">We couldn’t load bus times.</h2>
          <p className="mt-2 text-muted-foreground">
            Try again, or choose your stop again if your selection has changed.
          </p>
          <Button className="mt-5" onClick={() => actions.loadView()} data-jr-key="refresh-scheduled-buses">
            Try again
          </Button>
        </div>
      ) : !hasSelection ? (
        <div className="rounded-[14px] border border-border bg-card p-8 sm:p-10">
          <p className="text-lg font-medium text-foreground">Choose a stop to see scheduled buses.</p>
          <p className="mt-2 max-w-md text-muted-foreground">
            Your selected route and stop will be remembered here for the next time you visit.
          </p>
          <Button render={<Link to="/stops" />} className="mt-6" data-jr-key="choose-stop-cta">
            Choose a stop <ArrowRight className="size-4" />
          </Button>
        </div>
      ) : (
        <section aria-label="Scheduled buses">
          <div className="flex flex-col justify-between gap-5 border-b border-border pb-6 sm:flex-row sm:items-end">
            <div>
              <p className="text-sm font-medium text-primary">
                {route?.routeName ?? 'Selected route'} · {direction?.directionName ?? 'Selected direction'}
              </p>
              <h2 className="mt-2 font-heading text-3xl font-semibold tracking-[-0.03em]">
                {stop?.stopName ?? 'Selected stop'}
              </h2>
              <p className="mt-2 text-sm text-muted-foreground">
                Service day{' '}
                {seattleDate(view?.serviceDay, { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
              </p>
            </div>
            <Button
              variant="outline"
              onClick={() => view?.stopSelectionId && actions.refresh(view.stopSelectionId)}
              disabled={!snapshot.can({ type: 'stop-selection.refresh', stopSelectionId: view?.stopSelectionId ?? '' })}
              data-jr-key="refresh-scheduled-buses"
            >
              <RefreshCw className="size-4" /> Refresh
            </Button>
          </div>
          {view?.buses?.length ? (
            <div className="mt-5 divide-y divide-border rounded-[14px] border border-border bg-card">
              {view.buses
                .filter((bus): bus is NonNullable<typeof bus> => Boolean(bus))
                .map((bus, index) => (
                  <div
                    key={`${bus.scheduledTime ?? 'bus'}-${index}`}
                    className="flex items-center justify-between gap-5 px-5 py-5 sm:px-7"
                  >
                    <div>
                      <p className="font-heading text-3xl font-semibold tabular-nums tracking-[-0.03em]">
                        {seattleDate(bus.scheduledTime, { hour: 'numeric', minute: '2-digit' })}
                      </p>
                      <p className="mt-1 text-sm text-muted-foreground">
                        {bus.destinationLabel ?? 'Destination not listed'}
                      </p>
                    </div>
                    <p className={index === 0 ? 'font-medium text-primary' : 'text-sm text-muted-foreground'}>
                      {bus.minutesUntil == null
                        ? 'Scheduled'
                        : bus.minutesUntil <= 0
                          ? 'Due now'
                          : `in ${bus.minutesUntil} min`}
                    </p>
                  </div>
                ))}
            </div>
          ) : (
            <div className="mt-5 rounded-[14px] border border-dashed border-border p-8 text-muted-foreground">
              No scheduled buses are available for this stop today.
            </div>
          )}
        </section>
      )}
    </AppShell>
  );
}
